import React, { useEffect, useState } from 'react';
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faCalculator,
  faCode,
  faBullhorn,
  faGraduationCap,
  faHeartPulse,
  faBriefcase,
  faChartLine,
  faLaptopCode,
  faPaintBrush,
  faHandshake,
  faHeadset,
  faMicroscope,
  faSitemap,
  faUserTie,
  faClipboard,
  faClipboardList,
  faFlask,
  faBalanceScale,
  faMoneyBillWave,
  faUsersCog,
  faIndustry,
  faEllipsisH,
  faComments,
  faServer
} from "@fortawesome/free-solid-svg-icons";
import "./jobCategories.css";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchJobCategoryListAsync,
  selectJobCategoryList,
} from "./JobCategorySlice/JobCategorySlice";
import { SkeletonCircle, SkeletonText, Box } from '@chakra-ui/react';
import { NoDataFound } from '../../pages/CommonPublicPages/Basic/NoDataFound';

// Updated colorMap with complete Tailwind class names
const colorStyleMap = {
  "Accounting / Finance": {
    bgClass: "bg-green-100",
    textClass: "text-green-500"
  },
  "Engineering": {
    bgClass: "bg-blue-100",
    textClass: "text-blue-500"
  },
  "Marketing": {
    bgClass: "bg-red-100",
    textClass: "text-red-500"
  },
  "Education": {
    bgClass: "bg-yellow-100",
    textClass: "text-yellow-500"
  },
  "Healthcare": {
    bgClass: "bg-purple-100",
    textClass: "text-purple-500"
  },
  "Business": {
    bgClass: "bg-orange-100",
    textClass: "text-orange-500"
  },
  "Information Technology": {
    bgClass: "bg-gray-100",
    textClass: "text-gray-500"
  },
  "Design": {
    bgClass: "bg-pink-100",
    textClass: "text-pink-500"
  },
  "Human Resources": {
    bgClass: "bg-teal-100",
    textClass: "text-teal-500"
  },
  "Sales": {
    bgClass: "bg-indigo-100",
    textClass: "text-indigo-500"
  },
  "Customer Service": {
    bgClass: "bg-cyan-100",
    textClass: "text-cyan-500"
  },
  "Research": {
    bgClass: "bg-lime-100",
    textClass: "text-lime-500"
  },
  "Management": {
    bgClass: "bg-violet-100",
    textClass: "text-violet-500"
  },
  "Human Resources (HR)": {
    bgClass: "bg-slate-100",
    textClass: "text-slate-500"
  },
  "Administrative": {
    bgClass: "bg-emerald-100",
    textClass: "text-emerald-500"
  },
  // Newly Added
  "Research & Development (R&D)": {
    bgClass: "bg-fuchsia-100",
    textClass: "text-fuchsia-500"
  },
  "Legal & Compliance": {
    bgClass: "bg-rose-100",
    textClass: "text-rose-500"
  },
  "Finance & Accounting": {
    bgClass: "bg-amber-100",
    textClass: "text-amber-500"
  },
  "Marketing & Sales": {
    bgClass: "bg-orange-100",
    textClass: "text-orange-600"
  },
  "Administrative & Management": {
    bgClass: "bg-violet-100",
    textClass: "text-violet-600"
  },
  "Operations & Production": {
    bgClass: "bg-sky-100",
    textClass: "text-sky-500"
  },
  "Others": {
    bgClass: "bg-zinc-100",
    textClass: "text-zinc-500"
  },
  "Public Relations (PR) & Communications": {
    bgClass: "bg-cyan-100",
    textClass: "text-cyan-600"
  },
  "IT (Information Technology)": {
    bgClass: "bg-stone-100",
    textClass: "text-stone-500"
  }
};

const iconMap = {
  "Accounting / Finance": faCalculator,
  "Engineering": faCode,
  "Marketing": faBullhorn,
  "Education": faGraduationCap,
  "Healthcare": faHeartPulse,
  "Business": faChartLine,
  "Information Technology": faLaptopCode,
  "Design": faPaintBrush,
  "Human Resources": faHandshake,
  "Sales": faChartLine,
  "Customer Service": faHeadset,
  "Research": faMicroscope,
  "Management": faSitemap,
  "Human Resources (HR)": faUserTie,
  "Administrative": faClipboardList,
  // Newly Added
  "Research & Development (R&D)": faFlask,
  "Legal & Compliance": faBalanceScale,
  "Finance & Accounting": faMoneyBillWave,
  "Marketing & Sales": faBullhorn,
  "Administrative & Management": faUsersCog,
  "Operations & Production": faIndustry,
  "Others": faEllipsisH,
  "Public Relations (PR) & Communications": faComments,
  "IT (Information Technology)": faServer
};


export function JobCategories() {
  const dispatch = useDispatch();
  const categories = useSelector(selectJobCategoryList);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    dispatch(fetchJobCategoryListAsync())
      .then(() => {
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [dispatch]);

  // Function to render the job categories section
  const renderJobCategories = () => {
    if (isLoading) {
      // Skeleton Loader when data is loading
      return Array.from({ length: 6 }).map((_, index) => (
        <div className="flex flex-row justify-evenly px-3 gap-5" key={index}>
          <Box width={"xl"} padding="6" boxShadow="lg" bg="white">
            <SkeletonCircle size="10" />
            <SkeletonText
              mt="4"
              noOfLines={4}
              spacing="4"
              skeletonHeight="2"
            />
          </Box>
        </div>
      ));
    } else if (!categories || categories.length === 0) {
      // No data state using the NoDataFound component
      return (
        <div className="col-span-1 sm:col-span-2 lg:col-span-3">
          <NoDataFound
            title="No Job Categories Found"
            message="We couldn't find any job categories at the moment. Please check back later."
          />
        </div>
      );
    } else {
      // Actual Data Rendering
      return Array.isArray(categories) && categories.slice(0, 6).map((category, index) => {
        const icon = iconMap[category.title] || faBriefcase;
        const colorStyle = colorStyleMap[category.title] || {
          bgClass: "bg-blue-100",
          textClass: "text-blue-500"
        };

        return (
          <div
            key={category.uuid}
            className="group bg-white hover:shadow-lg border-gray-200 overflow-hidden inset-0 border-2 rounded-xl transition-all duration-300 hover:border-blue-500"
          >
            <Link to={`/job-categories/${category.uuid}`} className="block p-6 relative">
              <div className="flex items-start space-x-4">
                <div className={`flex-shrink-0 w-12 h-12 rounded-lg ${colorStyle.bgClass} flex items-center justify-center`}>
                  <FontAwesomeIcon
                    icon={icon}
                    className={`${colorStyle.textClass} text-xl`}
                  />
                </div>
                <div className="flex-grow">
                  <h3 className="text-lg text-start font-semibold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">
                    {category.title}
                  </h3>
                  <p className="text-gray-500 text-start text-sm mb-2">
                    ({category.job_count} opening positions)
                  </p>
                </div>
              </div>
            </Link>
          </div>
        );
      });
    }
  };

  return (
    <div className="py-12 job-categories">
      <div className="max-w-screen-xl mx-auto">
        <div className="text-center py-12">
          <h2 className="text-4xl font-semibold text-gray-900 mb-2">
            Most Searched <span className="text-green-600">Job Categories</span>
          </h2>
          <p className="text-gray-600 text-lg underline">
            {new Date().getFullYear()} jobs live - 293 added today.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 px-4 sm:px-0 gap-3">
          {renderJobCategories()}
        </div>
      </div>
    </div>
  );
}